import { AssetManifestClient, AudioBus, resolveEntryUrl } from '@goodtime/engine';
import {
  WORLD_NODE_TYPES,
  type Biome,
  type WorldMapNode,
  type WorldNodeType,
} from '@goodtime/shared';
import { Icon } from '../Icon';
import { darken, lighten } from './buildPath';

// Map-node badge art — the chunky illustrated coin medallions the
// toolgen pipeline drops in (e.g. `dragon_reels.png`, `slot.png`).
// Stage 3 install-excision (2026-06-06) — used to use a wildcard glob
// that bundled every {@code apps/mobile/src/assets/map_node/*.png}
// (~64MB across ~70 files, of which any single render uses one).
// Now resolved via the asset manifest + {@link resolveEntryUrl} so
// production fetches from CDN.
//
// Existence check in prod: the manifest tells us which types ship
// (toolgen-published assets). In dev the manifest is usually empty
// and we hand the browser a Vite-dev-server URL — if the file isn't
// on disk it's a single 404 logged in the console and the emoji
// fallback covers the visual. Acceptable trade-off; the glob's
// existence-probe was the only thing it offered over a path-only
// lookup, and the bundle cost was disproportionate.
function nodeBadge(type: string): string | undefined {
  const path = `map_node/${type}.png`;
  const manifest = AssetManifestClient.shared.getManifest();
  if (manifest && !manifest.entries[path]) return undefined;
  return resolveEntryUrl({ path }) ?? undefined;
}

/**
 * Per-type reward preview. Mirrors what each mini-game scene
 * actually awards so the player-facing promise on the node tap
 * matches what the scene delivers. Scenes with variable rewards
 * use a representative range; fixed-reward scenes use the exact
 * value.
 *
 * Sources:
 *   • daily   → DailyQuestScene.ts → fixed 50 / 1
 *   • chest   → fixtures/chestFixtures.ts → roll 100–500 / 1–3
 *   • scratch → ScratchModel rolls 25–250 / 0–3
 *   • boss    → big multiplier on the wheel; left vague
 *   • slot/wheel/bingo/card/mini → variable; left vague
 */
const REWARD_PREVIEW: Partial<
  Record<WorldNodeType, { coins?: string; gems?: string }>
> = {
  daily: { coins: '+50', gems: '+1' },
  chest: { coins: '+100–500', gems: '+1–3' },
  scratch: { coins: '+25–250', gems: '+0–3' },
};

export interface NodePreviewProps {
  node: WorldMapNode;
  biome: Biome;
  onClose: () => void;
  onPlay: (node: WorldMapNode) => void;
}

/**
 * Modal preview of a tapped map node — biome-themed gradient banner,
 * candy-coin icon, level + type label, reward chips, "friends played
 * here" cluster, and the headline PLAY button.
 *
 * <p>Locked nodes show a "Coming Soon" disabled state instead of PLAY.
 * Tapping outside the panel dismisses; the panel itself stops the
 * propagation so taps inside don't close.
 */
export function NodePreview({
  node,
  biome,
  onClose,
  onPlay,
}: NodePreviewProps) {
  const meta = WORLD_NODE_TYPES[node.type];
  const badge = nodeBadge(node.type);
  const isLocked = node.type === 'locked';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'gtFadeIn 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 300,
          background: '#fff',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
          animation: 'gtPop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          fontFamily: '"Fredoka", -apple-system, system-ui',
          border: '4px solid #fff',
        }}
      >
        {/* biome-themed banner */}
        <div
          style={{
            height: 110,
            position: 'relative',
            background: `linear-gradient(135deg, ${biome.sky[0]}, ${biome.sky[2]})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* sparkle dots */}
          <svg
            width="100%"
            height="100%"
            style={{ position: 'absolute', inset: 0, opacity: 0.5 }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <circle
                key={i}
                cx={`${(i * 23) % 100}%`}
                cy={`${(i * 37) % 100}%`}
                r="1.5"
                fill="#fff"
              />
            ))}
          </svg>

          {/* slot badge art — the chunky coin medallion; falls back to the
              emoji coin for node types without a generated badge */}
          {badge ? (
            <img
              src={badge}
              alt=""
              width={88}
              height={88}
              style={{
                objectFit: 'contain',
                filter: 'drop-shadow(0 10px 14px rgba(0,0,0,0.35))',
                position: 'relative',
                zIndex: 1,
              }}
            />
          ) : (
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `radial-gradient(circle at 35% 30%, ${lighten(meta.color, 0.4)}, ${meta.color} 55%, ${darken(meta.color, 0.2)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 42,
                boxShadow: `0 8px 0 ${darken(meta.color, 0.35)}, 0 12px 20px rgba(0,0,0,0.3)`,
                border: `4px solid #fff`,
                position: 'relative',
                zIndex: 1,
              }}
            >
              {meta.icon}
            </div>
          )}

          {/* Chapter/biome corner labels removed (producer 2026-06-08):
              they sat behind the slot badge and just duplicated the
              "Level N · NAME" + title shown in the body below. */}
        </div>

        {/* body */}
        <div style={{ padding: '16px 20px 18px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: meta.color,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
            }}
          >
            Level {node.id} · {meta.label}
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: '#1a1a2e',
              marginTop: 4,
              marginBottom: 12,
            }}
          >
            {node.name}
          </div>

          {!isLocked && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 14,
                flexWrap: 'wrap',
              }}
            >
              {(() => {
                const preview = REWARD_PREVIEW[node.type];
                const chips: React.ReactNode[] = [];
                if (preview?.coins) {
                  chips.push(
                    <RewardChip
                      key="coins"
                      label={
                        <>
                          <Icon name="coin" size={14} alt="" /> {preview.coins}
                        </>
                      }
                    />,
                  );
                }
                if (preview?.gems) {
                  chips.push(
                    <RewardChip
                      key="gems"
                      label={
                        <>
                          <Icon name="gem" size={14} alt="gems" /> {preview.gems}
                        </>
                      }
                    />,
                  );
                }
                if (chips.length === 0) {
                  // Variable-reward scene (slot / wheel / bingo / card /
                  // mini) — show a vague teaser instead of a misleading
                  // exact number until the scene's actual reward roll.
                  chips.push(
                    <RewardChip
                      key="vague"
                      label={
                        <>
                          <Icon name="coin" size={14} alt="" /> Coins ·{' '}
                          <Icon name="gem" size={14} alt="" /> Gems
                        </>
                      }
                    />,
                  );
                }
                if (node.type === 'boss') {
                  chips.push(
                    <RewardChip key="boss" label="👑 BOSS" tone="boss" />,
                  );
                }
                return chips;
              })()}
            </div>
          )}

          {/* "3 friends played here" social-proof strip removed —
              it's fake on session 1 with no real cohort. Re-add once
              real friend-activity data exists post-cohort launch. */}

          <button
            type="button"
            onClick={() => {
              if (!isLocked) {
                AudioBus.shared.play('ui:click_primary');
                onPlay(node);
              }
              onClose();
            }}
            disabled={isLocked}
            style={{
              width: '100%',
              height: 52,
              border: 'none',
              borderRadius: 16,
              background: isLocked
                ? '#E5E7EB'
                : `linear-gradient(180deg, ${lighten(meta.color, 0.2)}, ${meta.color})`,
              color: isLocked ? '#9CA3AF' : '#fff',
              fontSize: 18,
              fontWeight: 700,
              cursor: isLocked ? 'not-allowed' : 'pointer',
              boxShadow: isLocked
                ? 'none'
                : `0 5px 0 ${darken(meta.color, 0.35)}, 0 10px 20px rgba(0,0,0,0.18)`,
              fontFamily: 'inherit',
              letterSpacing: 0.4,
              textShadow: isLocked ? 'none' : '0 1px 2px rgba(0,0,0,0.25)',
            }}
          >
            {isLocked ? (
              <>
                <Icon name="lock" size={16} alt="" /> Coming Soon
              </>
            ) : (
              `PLAY  ▶`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function RewardChip({
  label,
  tone = 'default',
}: {
  label: React.ReactNode;
  tone?: 'default' | 'boss';
}) {
  const styles =
    tone === 'boss'
      ? { background: '#FFE4E8', color: '#E8344E', borderColor: '#F4A0AC' }
      : { background: '#FFF3D6', color: '#7A5A2A', borderColor: '#F4D88E' };
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '5px 12px',
        borderRadius: 12,
        background: styles.background,
        color: styles.color,
        fontSize: 13,
        fontWeight: 700,
        border: `2px solid ${styles.borderColor}`,
      }}
    >
      {label}
    </div>
  );
}
